import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useState, useLayoutEffect } from 'react';
import { resolveImageAltText } from '@/lib/utils';

// Avoid SSR for ReactPlayer to prevent hydration mismatches.
const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

export default function GalleryMedia({
  image,
  video,
  isActive,
  priority,
  playing,
  onPlay,
  onPause
}) {
  const [hasPlayedVideo, setHasPlayedVideo] = useState(false);
  const [remountKey, setRemountKey] = useState(0);

  // Load video when active, and keep it loaded once playback has started.
  const shouldLoadVideo = !!video && (isActive || hasPlayedVideo);

  function handlePlay() {
    if (!hasPlayedVideo) {
      setHasPlayedVideo(true);
    }
    onPlay?.();
  }

  // Bump the key to remount on Activity show, since Vimeo keeps
  // trying to pause itself otherwise.
  useLayoutEffect(() => {
    return () => {
      setRemountKey((key) => key + 1);
    };
  }, []);

  const playerConfig = {
    muted: true,
    playsInline: true,
    width: '100%',
    height: '100%',
    loop: true,
    controls: false,
    config: {
      vimeo: {
        playerOptions: {
          background: true,
          controls: false,
          byline: false,
          title: false,
          portrait: false,
          keyboard: false
        }
      }
    }
  };

  return (
    <div className="relative aspect-4/3 w-full overflow-hidden">
      {/* Poster */}
      <Image
        src={image.url}
        fill
        priority={priority}
        alt={resolveImageAltText(image) || ''}
        sizes="(min-width: 1510px) 888px, (min-width: 1024px) 457px, (min-width: 640px) 640px, 100vw"
        className={`object-cover transition-opacity duration-500`}
        fetchPriority={priority ? 'high' : 'low'}
      />

      {/* Video */}
      {video && shouldLoadVideo && (
        <ReactPlayer
          key={remountKey}
          url={video.url}
          playing={playing}
          onPlay={handlePlay}
          onPause={onPause}
          {...playerConfig}
          className="absolute inset-0"
        />
      )}
    </div>
  );
}
